import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid3x3, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * CollegeNavigationBar
 *
 * Navigation bar for individual college strategy pages with previous/next controls
 * and quick navigation dropdown grouped by tier (Reach/Target/Safety).
 *
 * Based on mockup in CJ-Wrapper-Nav.jsx
 */
const CollegeNavigationBar = ({
  currentCollegeId,
  colleges = [],
  onCollegeChange,
  onBackToGrid
}) => {
  const navigate = useNavigate();
  const [showQuickNav, setShowQuickNav] = useState(false);

  // Group colleges by category
  const collegesByTier = useMemo(() => {
    const reach = colleges.filter(c => c.category === 'Reach' || c.category === 'reach');
    const target = colleges.filter(c => c.category === 'Target' || c.category === 'target');
    const safety = colleges.filter(c => c.category === 'Safety' || c.category === 'safety');
    return { reach, target, safety };
  }, [colleges]);

  // Flatten for navigation
  const allColleges = useMemo(() => {
    return [...collegesByTier.reach, ...collegesByTier.target, ...collegesByTier.safety];
  }, [collegesByTier]);

  // Find current college index
  const currentIndex = allColleges.findIndex(c => c.id === currentCollegeId);
  const currentCollege = allColleges[currentIndex];

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const prevCollege = allColleges[currentIndex - 1];
      if (onCollegeChange) {
        onCollegeChange(prevCollege.id);
      } else {
        navigate(`/college/colleges/${prevCollege.id}`);
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < allColleges.length - 1) {
      const nextCollege = allColleges[currentIndex + 1];
      if (onCollegeChange) {
        onCollegeChange(nextCollege.id);
      } else {
        navigate(`/college/colleges/${nextCollege.id}`);
      }
    }
  };

  const handleQuickNav = (collegeId) => {
    setShowQuickNav(false);
    if (onCollegeChange) {
      onCollegeChange(collegeId);
    } else {
      navigate(`/college/colleges/${collegeId}`);
    }
  };

  const handleBackToGrid = () => {
    if (onBackToGrid) {
      onBackToGrid();
    } else {
      navigate('/college/colleges');
    }
  };

  const getCategoryColor = (category) => {
    const cat = category?.toLowerCase();
    switch(cat) {
      case 'reach': return 'border-coral-500 bg-coral-50';
      case 'target': return 'border-gold-500 bg-gold-50';
      case 'safety': return 'border-teal-500 bg-teal-50';
      default: return 'border-slate-300 bg-stone-50';
    }
  };

  const getCategoryTextColor = (category) => {
    const cat = category?.toLowerCase();
    switch(cat) {
      case 'reach': return 'text-coral-700';
      case 'target': return 'text-gold-700';
      case 'safety': return 'text-teal-700';
      default: return 'text-slate-700';
    }
  };

  const getCategoryHeaderColor = (category) => {
    const cat = category?.toLowerCase();
    switch(cat) {
      case 'reach': return 'bg-coral-50 border-coral-200 text-coral-700';
      case 'target': return 'bg-gold-50 border-gold-200 text-gold-700';
      case 'safety': return 'bg-teal-50 border-teal-200 text-teal-700';
      default: return 'bg-stone-50 border-stone-200 text-stone-700';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showQuickNav && !event.target.closest('.quick-nav-dropdown')) {
        setShowQuickNav(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQuickNav]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && currentIndex > 0) {
        handlePrevious();
      } else if (event.key === 'ArrowRight' && currentIndex < allColleges.length - 1) {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, allColleges.length]);

  if (!currentCollege || allColleges.length === 0) {
    return null;
  }

  return (
    <div className="bg-slate-800 border-2 border-slate-900 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 shadow-lg">
      <div className="flex items-center justify-between">

        {/* Left: Back to Grid */}
        <button
          onClick={handleBackToGrid}
          className="flex items-center gap-1.5 sm:gap-2 text-stone-50 hover:text-white transition-colors"
        >
          <Grid3x3 className="w-4 h-4" />
          <span className="text-xs sm:text-sm font-medium hidden sm:inline">My Colleges</span>
          <span className="text-xs sm:text-sm font-medium sm:hidden">Grid</span>
        </button>

        {/* Center: College Browser */}
        <div className="flex items-center gap-2 sm:gap-4">

          {/* Previous Button */}
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className={`p-1.5 sm:p-2 rounded-lg border-2 transition-all ${
              currentIndex === 0
                ? 'border-stone-600 text-stone-500 cursor-not-allowed'
                : 'border-slate-900 text-stone-50 hover:bg-slate-700 active:scale-95'
            }`}
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          {/* Current College Indicator with Quick Nav */}
          <div className="relative quick-nav-dropdown">
            <button
              onClick={() => setShowQuickNav(!showQuickNav)}
              className="px-3 sm:px-6 py-1.5 sm:py-2 bg-stone-50 border border-slate-900 rounded-lg hover:bg-stone-100 transition-colors"
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <span className={`text-xs font-bold uppercase tracking-wide ${getCategoryTextColor(currentCollege.category)}`}>
                  <span className="hidden sm:inline">{currentCollege.category}</span>
                  <span className="sm:hidden">{currentCollege.category.charAt(0)}</span>
                </span>
                <span className="text-xs sm:text-sm text-slate-900 font-medium">
                  {currentIndex + 1}/{allColleges.length}
                </span>
              </div>
            </button>

            {/* Quick Navigation Dropdown */}
            {showQuickNav && (
              <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-white border-2 border-slate-900 rounded-lg shadow-xl z-50 min-w-[300px] max-h-[400px] overflow-y-auto">

                {/* Reach Schools */}
                {collegesByTier.reach.length > 0 && (
                  <div className="border-b border-stone-200">
                    <div className={`px-4 py-2 border-b ${getCategoryHeaderColor('reach')}`}>
                      <span className="text-xs font-bold uppercase tracking-wide">Reach Schools</span>
                    </div>
                    {collegesByTier.reach.map((college) => (
                      <button
                        key={college.id}
                        onClick={() => handleQuickNav(college.id)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-stone-50 transition-colors ${
                          college.id === currentCollegeId ? 'bg-stone-100 font-medium' : ''
                        }`}
                      >
                        <div className="font-medium">{college.name || college.college_name}</div>
                        {college.location && (
                          <div className="text-xs text-stone-500">{college.location}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Target Schools */}
                {collegesByTier.target.length > 0 && (
                  <div className="border-b border-stone-200">
                    <div className={`px-4 py-2 border-b ${getCategoryHeaderColor('target')}`}>
                      <span className="text-xs font-bold uppercase tracking-wide">Target Schools</span>
                    </div>
                    {collegesByTier.target.map((college) => (
                      <button
                        key={college.id}
                        onClick={() => handleQuickNav(college.id)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-stone-50 transition-colors ${
                          college.id === currentCollegeId ? 'bg-stone-100 font-medium' : ''
                        }`}
                      >
                        <div className="font-medium">{college.name || college.college_name}</div>
                        {college.location && (
                          <div className="text-xs text-stone-500">{college.location}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Safety Schools */}
                {collegesByTier.safety.length > 0 && (
                  <div>
                    <div className={`px-4 py-2 border-b ${getCategoryHeaderColor('safety')}`}>
                      <span className="text-xs font-bold uppercase tracking-wide">Safety Schools</span>
                    </div>
                    {collegesByTier.safety.map((college) => (
                      <button
                        key={college.id}
                        onClick={() => handleQuickNav(college.id)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-stone-50 transition-colors ${
                          college.id === currentCollegeId ? 'bg-stone-100 font-medium' : ''
                        }`}
                      >
                        <div className="font-medium">{college.name || college.college_name}</div>
                        {college.location && (
                          <div className="text-xs text-stone-500">{college.location}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Next Button */}
          <button
            onClick={handleNext}
            disabled={currentIndex === allColleges.length - 1}
            className={`p-1.5 sm:p-2 rounded-lg border-2 transition-all ${
              currentIndex === allColleges.length - 1
                ? 'border-stone-600 text-stone-500 cursor-not-allowed'
                : 'border-slate-900 text-stone-50 hover:bg-slate-700 active:scale-95'
            }`}
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Right: Keyboard Hint - Hidden on mobile */}
        <div className="hidden lg:flex items-center gap-2 text-xs text-stone-100">
          <kbd className="px-2 py-1 bg-stone-100 border text-slate-900 border-stone-100 rounded">←</kbd>
          <kbd className="px-2 py-1 bg-stone-100 border text-slate-900 border-stone-100 rounded">→</kbd>
          <span>to navigate</span>
        </div>
      </div>
    </div>
  );
};

export default CollegeNavigationBar;
